/**
 * Performance Monitoring with Web Vitals
 * 
 * Tracks Core Web Vitals metrics:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - INP (Interaction to Next Paint): Overall responsiveness (replaces FID)
 * - FCP (First Contentful Paint): Perceived load speed
 * - LCP (Largest Contentful Paint): Main content load
 * - TTFB (Time to First Byte): Server response time
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

export interface PerformanceMetric {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType: string;
    timestamp: number;
    url: string;
    userAgent: string;
}

/**
 * Get rating thresholds for each metric
 */
const THRESHOLDS = {
    CLS: { good: 0.1, needsImprovement: 0.25 },
    FCP: { good: 1800, needsImprovement: 3000 },
    LCP: { good: 2500, needsImprovement: 4000 },
    TTFB: { good: 800, needsImprovement: 1800 },
    INP: { good: 200, needsImprovement: 500 },
} as const;

/**
 * Determine rating based on metric value
 */
function getRating(
    name: keyof typeof THRESHOLDS,
    value: number
): 'good' | 'needs-improvement' | 'poor' {
    const threshold = THRESHOLDS[name];
    if (value <= threshold.good) return 'good';
    if (value <= threshold.needsImprovement) return 'needs-improvement';
    return 'poor';
}

/**
 * Format metric for logging/analytics
 */
function formatMetric(metric: Metric): PerformanceMetric {
    return {
        name: metric.name,
        value: metric.value,
        rating: metric.rating as 'good' | 'needs-improvement' | 'poor',
        delta: metric.delta,
        id: metric.id,
        navigationType: metric.navigationType,
        timestamp: Date.now(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
}

/**
 * Send metric to analytics endpoint
 */
async function sendToAnalytics(metric: PerformanceMetric): Promise<void> {
    try {
        // Use sendBeacon if available (non-blocking)
        if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
            const blob = new Blob([JSON.stringify(metric)], {
                type: 'application/json',
            });
            navigator.sendBeacon('/api/analytics/performance', blob);
        } else {
            // Fallback to fetch with keepalive
            await fetch('/api/analytics/performance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(metric),
                keepalive: true,
            });
        }
    } catch (error) {
        // Fail silently in production, don't disrupt user experience
        if (process.env.NODE_ENV === 'development') {
            console.error('Failed to send performance metric:', error);
        }
    }
}

/**
 * Log metric to console in development
 */
function logMetric(metric: PerformanceMetric): void {
    if (process.env.NODE_ENV === 'development') {
        const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
    }
}

/**
 * Handle metric reporting
 */
function handleMetric(metric: Metric): void {
    const formattedMetric = formatMetric(metric);

    // Log in development
    logMetric(formattedMetric);

    // Send to analytics in production
    if (process.env.NODE_ENV === 'production') {
        sendToAnalytics(formattedMetric);
    }
}

/**
 * Initialize Web Vitals monitoring
 * 
 * Call this once in your app, typically in root layout or _app.tsx
 */
export function initializePerformanceMonitoring(): void {
    // Only run in browser
    if (typeof window === 'undefined') return;

    try {
        // Track all Core Web Vitals
        onCLS(handleMetric);
        onFCP(handleMetric);
        onLCP(handleMetric);
        onTTFB(handleMetric);

        // Track INP (replaces deprecated FID)
        onINP(handleMetric);

        // Log initialization in development
        if (process.env.NODE_ENV === 'development') {
        }
    } catch (error) {
        // Fail silently
        if (process.env.NODE_ENV === 'development') {
            console.error('Failed to initialize performance monitoring:', error);
        }
    }
}

/**
 * Get current performance metrics snapshot
 * Useful for debugging or manual reporting
 */
export function getPerformanceSnapshot(): Record<string, number> {
    if (typeof window === 'undefined') return {};

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');

    return {
        // Navigation timing
        dns: navigation?.domainLookupEnd - navigation?.domainLookupStart,
        tcp: navigation?.connectEnd - navigation?.connectStart,
        ttfb: navigation?.responseStart - navigation?.requestStart,
        download: navigation?.responseEnd - navigation?.responseStart,
        domInteractive: navigation?.domInteractive,
        domComplete: navigation?.domComplete,
        loadComplete: navigation?.loadEventEnd,

        // Paint timing
        fcp: paint.find((entry) => entry.name === 'first-contentful-paint')?.startTime || 0,

        // Memory (if available)
        jsHeapSize: (performance as any).memory?.usedJSHeapSize || 0,
        jsHeapLimit: (performance as any).memory?.jsHeapSizeLimit || 0,
    };
}

/**
 * Mark custom performance events
 * Useful for tracking custom interactions
 */
export function markPerformance(name: string): void {
    if (typeof window === 'undefined') return;

    try {
        performance.mark(name);

        if (process.env.NODE_ENV === 'development') {
        }
    } catch (error) {
        // Fail silently
    }
}

/**
 * Measure time between two performance marks
 */
export function measurePerformance(name: string, startMark: string, endMark: string): number {
    if (typeof window === 'undefined') return 0;

    try {
        const measure = performance.measure(name, startMark, endMark);

        if (process.env.NODE_ENV === 'development') {
        }

        return measure.duration;
    } catch (error) {
        return 0;
    }
}
